import React, { useContext, useEffect, useState } from "react";
import { Toaster, toast } from "react-hot-toast";
import Jobs from "../common/Jobs/Jobs";
import CircularLoader from "../../Ui/Loader";
import ButtonComponent from "../../Ui/Button";
import useApiHandler from "../../hooks/ApiHandler";
import { UserContext } from "../../context/UserContext";
import { searchbarVisibilty } from "../../constant/Home/Dashboard";
import { useCurrentSelectedData } from "../../context/CurrentSelectedDataContext";
import WarrantyClaimReviewModal from "../common/ModalData/WarrantyClaimReviewModal";
import WarrantyScheduleRepairModal from "../common/ModalData/WarrantyScheduleRepairModal";
import { JobsApi, WarrantyRequests } from "../../../service";

const SCHEDULE_WARRANTY_MODAL = "scheduleWarrantyRepair";
const VIEW_WARRANTY_DETAILS_MODAL = "viewWarrantyDetails";

const getApiErrorMessage = (error, fallbackMessage) => {
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim()) return data;
  if (data?.detail) return data.detail;

  if (data && typeof data === "object") {
    const firstValue = Object.values(data)[0];
    if (Array.isArray(firstValue) && firstValue[0]) return firstValue[0];
    if (typeof firstValue === "string" && firstValue.trim()) return firstValue;
  }

  return fallbackMessage;
};

const PastJobs = () => {
  const { userData } = useContext(UserContext);
  const { callApi, loading } = useApiHandler();
  const { currentSelectedData, setCurrentSelectedData } = useCurrentSelectedData();
  const [jobs, setJobs] = React.useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [resultsPerPage, setResultsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [modalState, setModalState] = useState("");
  const [warrantyClaim, setWarrantyClaim] = useState(null);
  const [reviewNotes, setReviewNotes] = useState("");
  const [claimLoading, setClaimLoading] = useState(false);
  const [decisionLoading, setDecisionLoading] = useState(false);
  const [scheduleSubmitting, setScheduleSubmitting] = useState(false);

  const getPastJobs = async (opts = {}) => {
    const { silent } = opts;
    await callApi(() => JobsApi.getPastJobs(currentPage, resultsPerPage), {
      silent,
      onSuccess: (response) => {
        if (response.data) {
          setJobs(response.data?.results || response.data);
          setTotalCount(response.data?.count || response.data?.length || 0);
        }
      },
      onError: (error) => {
        console.log(error);
      },
    });
  };

  const resetModalState = () => {
    setModalState("");
    setWarrantyClaim(null);
    setReviewNotes("");
    setClaimLoading(false);
    setDecisionLoading(false);
    setScheduleSubmitting(false);
  };

  const loadWarrantyClaim = async (claimId) => {
    if (!claimId) return;

    setClaimLoading(true);
    setWarrantyClaim(null);
    setReviewNotes("");

    try {
      await callApi(() => WarrantyRequests.getById(claimId), {
        silent: true,
        onSuccess: (response) => {
          const data = response?.data || response;
          setWarrantyClaim(data);
          setReviewNotes(data?.reviewNotes || "");
        },
        onError: (error) => {
          toast.error(getApiErrorMessage(error, "Could not load warranty details."));
          resetModalState();
        },
      });
    } finally {
      setClaimLoading(false);
    }
  };

  const handleResultsPerPageChange = (newResultsPerPage) => {
    setResultsPerPage(newResultsPerPage);
    setCurrentPage(1);
  };

  useEffect(() => {
    getPastJobs();
  }, [currentPage, resultsPerPage]);

  useEffect(() => {
    const onPoll = () => {
      getPastJobs({ silent: true }).catch(() => {});
    };
    window.addEventListener("dashboard:poll", onPoll);
    return () => window.removeEventListener("dashboard:poll", onPoll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, resultsPerPage]);

  const handleScheduleWarrantyClick = (job) => {
    setCurrentSelectedData(job);
    setModalState(SCHEDULE_WARRANTY_MODAL);
  };

  const handleViewWarrantyDetailsClick = async (job) => {
    setCurrentSelectedData(job);
    setModalState(VIEW_WARRANTY_DETAILS_MODAL);
    const claimId = job?.activeWarrantyClaim?.id || job?.active_warranty_claim?.id;
    await loadWarrantyClaim(claimId);
  };

  const handleClose = () => {
    resetModalState();
  };

  const handleWarrantyDecision = async (decision) => {
    if (!warrantyClaim?.id) return;
    if (decision === "deny" && !reviewNotes.trim()) {
      toast.error("Add a reason before denying this claim.");
      return;
    }

    setDecisionLoading(true);
    try {
      await callApi(
        () =>
          decision === "approve"
            ? WarrantyRequests.approve(warrantyClaim.id, { review_notes: reviewNotes })
            : WarrantyRequests.deny(warrantyClaim.id, { review_notes: reviewNotes.trim() }),
        {
          silent: true,
          onSuccess: async (response) => {
            const data = response?.data || response;
            toast.success(
              decision === "approve"
                ? "Warranty claim approved. Schedule the repair to continue."
                : "Warranty claim denied."
            );
            if (decision === "approve") {
              setWarrantyClaim(data);
              setReviewNotes(data?.reviewNotes || reviewNotes);
            } else {
              resetModalState();
            }
            await getPastJobs({ silent: true });
          },
          onError: (error) => {
            toast.error(
              getApiErrorMessage(
                error,
                decision === "approve"
                  ? "Could not approve the warranty claim."
                  : "Could not deny the warranty claim."
              )
            );
          },
        }
      );
    } finally {
      setDecisionLoading(false);
    }
  };

  const statusKey = String(warrantyClaim?.status || "").toUpperCase();
  const canApproveOrDeny = statusKey === "SUBMITTED";
  const canScheduleFromDetails = statusKey === "APPROVED";

  return (
    <>
      <Toaster />
      {loading ? (
        <CircularLoader minHeight="400px" />
      ) : jobs.length ? (
        <Jobs
          title="Past Jobs"
          jobs={jobs}
          search={searchbarVisibilty[userData.accountType]?.pastJobs}
          onScheduleWarrantyClick={handleScheduleWarrantyClick}
          onViewWarrantyDetailsClick={handleViewWarrantyDetailsClick}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          resultsPerPage={resultsPerPage}
          onResultsPerPageChange={handleResultsPerPageChange}
          totalCount={totalCount}
        />
      ) : (
        <div className="text-center py-8 text-[#595959]">
          <p className="text-[16px] font-[500]">No past jobs at the moment.</p>
          <p className="text-[14px] mt-2">
            All your completed jobs will appear here once they are finished.
          </p>
        </div>
      )}

      <WarrantyClaimReviewModal
        open={modalState === VIEW_WARRANTY_DETAILS_MODAL}
        onClose={handleClose}
        claim={warrantyClaim}
        loading={claimLoading}
        reviewNotes={reviewNotes}
        onReviewNotesChange={setReviewNotes}
        allowReview={canApproveOrDeny}
        extraFooterActions={
          canScheduleFromDetails ? (
            <ButtonComponent
              bgColor="#009FD4"
              color="#FFFFFF"
              borderColor="#009FD4"
              paddingY="10px"
              onClick={() => setModalState(SCHEDULE_WARRANTY_MODAL)}
            >
              Schedule Warranty Repair
            </ButtonComponent>
          ) : null
        }
        onApprove={() => handleWarrantyDecision("approve")}
        onDeny={() => handleWarrantyDecision("deny")}
        decisionLoading={decisionLoading}
      />

      <WarrantyScheduleRepairModal
        open={modalState === SCHEDULE_WARRANTY_MODAL}
        onClose={handleClose}
        onSubmit={async (values) => {
          const payload = {
            rescheduleReason: values.rescheduleReason,
            daysAvailable: values.days_available,
          };

          setScheduleSubmitting(true);
          try {
            await callApi(
              () => JobsApi.proposeReschedule(currentSelectedData?.id, payload),
              {
                silent: true,
                onSuccess: async () => {
                  toast.success("Warranty schedule request sent to customer.");
                  resetModalState();
                  await getPastJobs({ silent: true });
                },
                onError: (error) => {
                  toast.error(
                    getApiErrorMessage(
                      error,
                      "Could not submit the warranty schedule request."
                    )
                  );
                },
              }
            );
          } finally {
            setScheduleSubmitting(false);
          }
        }}
        submitting={scheduleSubmitting}
      />
    </>
  );
};

export default PastJobs;
